/**
 *
 * @description  模板列表页的详情
 *
 * */

import { useEffect, useRef, useState } from 'react';
import { Button, Tooltip } from 'antd';
import { useEventEmitter } from '&/utils/customHooks/useEventEmitter';
import ConstructionTemplate from '&/components/TemplateManagement/ConstructionTemplate';
import { useStoreState } from '&/utils/customHooks';
import EditConstructionTemplateModal from './EditConstructionTemplateModal';
import DefaultEmpty from '&/components/TemplateManagement/DefaultEmpty';
import Template from './Template';
import AddAndEditModal from './Modal';
import { deepCopy } from '&/utils/common';
import {
    indexMap,
    getCheckData,
    getConstructionData,
    getPollingData,
    getDecorateToDoData,
} from '&/components/TemplateManagement/common';
import EditTaskTemplate from './EditTaskTemplate';
import SvgIcon from '&/components/SvgIcon';
import './TemplateDetail.scss';

export default function TemplateDetail() {
    const { useListener } = useEventEmitter();
    const { organization = {} } = useStoreState('appStore');

    const addAndEditModalRef = useRef();
    const editConstructionTemplateModalRef = useRef();

    const [title, setTitle] = useState('');
    const [templateType, setTemplateType] = useState('');
    const [templateId, setTemplateId] = useState(null);
    const [templateData, setTemplateData] = useState(null);
    const [templateDetailData, setTemplateDetailData] = useState({});
    const [menuList, setMenuList] = useState(null);
    const [linkListMap, setLinkListMap] = useState({});

    const [noEdit, setNoEdit] = useState(false);

    useEffect(() => {
        changeEditDisabled();
    }, [menuList, templateType]);

    useListener('selected-template-node', ([node]) => {
        if (!node.isLeaf && node.children) {
            const nodeData = node.children[0];
            if (nodeData) {
                initNode(nodeData);
            } else {
                initNode({ parentName: node.name, type: node.type });
            }
        } else {
            initNode(node);
        }
        if (node.type === 'constructionTemplate') {
            const listMap = {
                decorateToDoList: node.treeData[1].children,
                pollingList: node.treeData[2].children,
                acceptanceStandardList: node.treeData[3].children,
            };
            setLinkListMap(listMap);
        }
        let treeList = node.treeData[indexMap[node.type]]?.children || [];
        if (node.type === 'constructionTemplate') {
            treeList = formatTreeList(treeList);
        }
        setMenuList(treeList);
    });
    useListener('update-template-list', ([data]) => {
        if (templateType) {
            let treeList = data.list[indexMap[templateType]]?.children || [];
            if (templateType === 'constructionTemplate') {
                treeList = formatTreeList(treeList);
            }
            setMenuList(treeList);
        }
    });

    const formatTreeList = treeList => {
        return deepCopy(treeList).map(item => {
            item.isLeaf = false;
            item.children =
                item.originalItem?.son?.map(sItem => {
                    return {
                        originalItem: { ...sItem, parentTemplateId: item.id },
                        name: sItem.name,
                        isLeaf: true,
                        id: sItem.id,
                        key: `${item.key}-${sItem.id}`,
                    };
                }) || [];
            return { ...item };
        });
    };

    const initNode = nodeData => {
        setTitle(nodeData.parentName);
        setTemplateType(nodeData.type);
        setTemplateId(nodeData.id);
        queryTemplateDetail(nodeData);
    };
    const queryTemplateDetail = nodeData => {
        if (nodeData.id) {
            let data = null;
            if (nodeData.type === 'acceptanceTemplate') {
                data = getCheckData(nodeData);
            } else if (nodeData.type === 'constructionTemplate') {
                data = getConstructionData(nodeData);
            } else if (nodeData.type === 'decorateTemplate') {
                data = getDecorateToDoData(nodeData);
            } else if (nodeData.type === 'inspectionTemplate') {
                data = getPollingData(nodeData);
            }
            setTemplateData(data);
        } else {
            setTemplateData({});
        }
    };
    const [visible, setVisible] = useState(false);

    const showModal = data => {
        setTemplateDetailData(data);

        // 除施工模板外的编辑和新增按钮
        if (templateType !== 'constructionTemplate') {
            return addAndEditModalRef.current.show();
        }

        // 施工模板
        //  编辑模板或者新增模板
        data ? setVisible(true) : showConstructionModal();
    };

    const showEditConstructionModal = list => {
        const data = list[0];
        const newData = data.children[data.children.length - 1];
        const nodeData = getConstructionData(newData);
        showModal(nodeData);
    };
    const showConstructionModal = () => {
        editConstructionTemplateModalRef.current.show();
    };
    const changeEditDisabled = () => {
        if (menuList) setNoEdit(!menuList?.length);
    };

    return (
        <div className="Template-detail">
            <div className="header">
                <div className="Template-detail-title">{title}</div>
                {organization.roleFlag === 'admin' && (
                    <div className="btn-box">
                        <Tooltip
                            placement="top"
                            title={
                                noEdit &&
                                intl.get('templateSettings.emptyTemplateEdit')
                            }>
                            <Button
                                className="edit-btn"
                                onClick={() => {
                                    showModal(templateData);
                                }}
                                disabled={noEdit}>
                                {intl.get('base.edit')}
                            </Button>
                        </Tooltip>
                        <Button
                            className="add-btn"
                            type="primary"
                            icon={<SvgIcon iconName="Icon-Add-Floor-Palns" />}
                            onClick={() => showModal()}
                            disabled={false}>
                            {`${intl.get('templateSettings.added')}${title}`}
                        </Button>
                    </div>
                )}
            </div>
            <div className="template-content-box">
                {templateId && (
                    <>
                        {templateType === 'constructionTemplate' && (
                            <ConstructionTemplate
                                templateList={templateData}
                                setTemplateData={setTemplateData}
                                linkListMap={linkListMap}
                            />
                        )}
                        {templateType !== 'constructionTemplate' && (
                            <Template
                                type={templateType}
                                templateData={templateData}
                            />
                        )}
                    </>
                )}
                {!templateId && (
                    <div className="empty-box">
                        <DefaultEmpty
                            option={{
                                title: intl.get(
                                    'templateSettings.emptyTemplate'
                                ),
                                addText: `${intl.get(
                                    'templateSettings.added'
                                )}${title}`,
                                addClick:
                                    organization.roleFlag === 'admin'
                                        ? () => showModal()
                                        : null,
                                ghost: true,
                            }}
                        />
                    </div>
                )}
            </div>
            <AddAndEditModal
                ref={addAndEditModalRef}
                templateData={{
                    templateDetailData,
                    templateType,
                    title,
                    menuList,
                }}
            />
            <EditConstructionTemplateModal
                showEditModal={showEditConstructionModal}
                ref={editConstructionTemplateModalRef}
            />

            <EditTaskTemplate
                visible={visible}
                setVisible={setVisible}
                templateList={templateData}
                menuList={menuList}
                templateData={{
                    templateDetailData,
                    templateType,
                    title,
                }}
            />
        </div>
    );
}
